import pageSchemas from '@/content/page-schemas.generated.json';
import { isRecord, PageDefinition } from '@/content/schema';

/** A field of an older page: the site reads it as `{ type, value }`. */
export interface LegacyField {
  type: 'string' | 'html';
  value: string;
}

/** How the CMS shows a field of an older page. */
export interface LegacyFieldInfo {
  key: string;
  type: LegacyField['type'];
  label: string;
  help: string;
  /** The site's own text, shown greyed out in the empty field. */
  placeholder?: string;
  /**
   * Starting value of a field the page has not saved yet: the site's current text, so it can be edited
   * or cleared. Used where empty means "none" instead of "the site's text".
   */
  initial?: string;
  /** Starts a new part of the form, with a heading. */
  section?: { title: string; help?: string };
  /** Width on a wide screen, of 12; full width by default. Phones always get the full width. */
  cols?: 3 | 6;
}

export interface ContentPage {
  name: string;
  slug: string;
  /** Address on the site; empty when it depends on the content (see `sitePath`). */
  path: string;
  /** Pages with a schema on the site get a generated form. */
  definition?: PageDefinition;
  /** Older pages: flat fields, stored as `{ key: { type, value } }`. */
  legacyFields?: LegacyFieldInfo[];
  /** Other names the page goes by on the site, so the page picker finds it by those too. */
  keywords: string[];
}

export interface PageGroup {
  title: string;
  pages: ContentPage[];
}

const definitions = pageSchemas as Record<string, PageDefinition>;

const html = (label: string, help: string): LegacyFieldInfo => ({ key: 'content', type: 'html', label, help });

const UNAVAILABLE = 'Leeg = de pagina zegt dat de tekst tijdelijk niet beschikbaar is.';

/** The line under the title of a "Wat is desinformatie" page, starting from the site's text. */
const intro = (initial: string): LegacyFieldInfo => ({ key: 'intro', type: 'string', label: 'Intro', help: 'De zin onder de titel. Leeg = geen intro.', initial });

const EMPTY_KEEPS = 'Een leeg veld toont de grijze tekst.';

/** A question of the tip form and the text in its empty field, side by side. */
const formField = (key: string, label: string, placeholder: string, help = ''): LegacyFieldInfo[] => [
  { key: `${key}-label`, type: 'string', label: 'Vraag', help, placeholder: label, cols: 6 },
  { key: `${key}-placeholder`, type: 'string', label: 'Tekst in het lege veld', help: '', placeholder, cols: 6 },
];

const LEGACY: Record<string, { name: string; path: string; fields: LegacyFieldInfo[] }> = {
  'factcheck-suggereren': {
    name: 'Tip ons',
    path: '/factcheck-suggereren',
    fields: [
      {
        key: 'title-accent', type: 'string', label: 'Titel: groen woord', help: '', placeholder: 'Echt', cols: 3,
        section: { title: 'Scherm 1: link plakken', help: `Het blok "Echt of bullshit?" op de homepage en op Tip ons. ${EMPTY_KEEPS}` },
      },
      { key: 'title-middle', type: 'string', label: 'Titel: woorden ertussen', help: '', placeholder: 'of', cols: 3 },
      { key: 'title-marked', type: 'string', label: 'Titel: roze markering', help: '', placeholder: 'bullshit', cols: 3 },
      { key: 'title-end', type: 'string', label: 'Titel: einde', help: 'Direct na het roze woord.', placeholder: '?', cols: 3 },
      { key: 'intro', type: 'string', label: 'Tekst onder de titel', help: '', placeholder: 'Heb je iets online gezien waar je aan twijfelt? Wij laten het checken voor jou.' },
      { key: 'link-placeholder', type: 'string', label: 'Tekst in het lege linkveld', help: '', placeholder: 'Plaats hier een link naar een artikel, foto of video' },
      ...formField('form-claim', 'Welke bewering wil je laten checken?', 'Beschrijf kort de uitspraak of post').map((field, index) => (
        index ? field : { ...field, section: { title: 'Scherm 2: formulier', help: `Opent na het plakken van een link. ${EMPTY_KEEPS}` } }
      )),
      ...formField('form-claimant', 'Wie doet deze bewering?', 'Naam van persoon of organisatie'),
      ...formField('form-source', 'Waar heb je dit gezien?', 'Link of platform (bv: sociale media)'),
      ...formField('form-arg', 'Waarom twijfel je hieraan?', 'Leg kort uit waarom je dit verdacht vindt'),
      ...formField('form-email', 'Jouw e-mailadres', 'naam@email.com'),
      ...formField('form-name', 'Jouw naam', 'Voor- en achternaam', 'De site zet er zelf "(optioneel)" achter.'),
      { key: 'form-photo-button', type: 'string', label: 'Knop afbeelding', help: '', placeholder: 'Afbeelding toevoegen', cols: 6 },
      { key: 'form-submit-button', type: 'string', label: 'Verzendknop', help: '', placeholder: 'Verzenden', cols: 6 },
      {
        key: 'thanks-title', type: 'string', label: 'Titel', help: '', placeholder: 'Bedankt!', cols: 6,
        section: { title: 'Scherm 3: na verzenden', help: EMPTY_KEEPS },
      },
      { key: 'thanks-again-button', type: 'string', label: 'Knop', help: '', placeholder: 'Laat iets anders checken', cols: 6 },
      {
        key: 'thanks-text', type: 'string', label: 'Tekst', help: '',
        placeholder: 'Jouw tip wordt doorgestuurd naar de verschillende factcheckredacties waar we mee samenwerken. Zodra jouw tip gecheckt is, verschijnt hij op onze pagina ‘Alle factchecks’.',
      },
      {
        key: 'explanation', type: 'html', label: 'Uitleg', help: 'Leeg = de standaarduitleg van de site.',
        section: { title: 'Onder het formulier', help: 'Alleen op de pagina Tip ons, niet op de homepage.' },
      },
      { key: 'whatsapp-link', type: 'string', label: 'WhatsApp-link', help: 'Bv. https://wa.me/32470000000. Leeg = geen WhatsApp-knop.' },
    ],
  },
  privacy: { name: 'Privacybeleid', path: '/privacy', fields: [html('Tekst', UNAVAILABLE)] },
  cookies: { name: 'Cookiebeleid', path: '/cookies', fields: [html('Tekst', UNAVAILABLE)] },
  'desinformatie-wat-is-het': {
    name: 'Wat is het?', path: '/wat-is-desinformatie/wat-is-het', fields: [intro('Wat we bedoelen met desinformatie, en waarom het ertoe doet.'), html('Tekst', '')],
  },
  'desinformatie-desinformatie-vs-misinformatie': {
    name: 'Desinformatie vs misinformatie', path: '/wat-is-desinformatie/desinformatie-vs-misinformatie',
    fields: [intro('Het verschil zit in de bedoeling: bewust misleiden of zich vergissen.'), html('Tekst', '')],
  },
  'desinformatie-waarom-geloven-we-het': {
    name: 'Waarom geloven we het?', path: '/wat-is-desinformatie/waarom-geloven-we-het',
    fields: [intro('Hoe ons brein ons vatbaar maakt voor berichten die te mooi of te erg zijn om waar te zijn.'), html('Tekst', '')],
  },
};

/** Site addresses of the pages with a schema; The bigger picture has none of its own. */
const PATHS: Record<string, string> = {
  home: '/', footer: '/', blokken: '/hoe-herken-je-ai-beelden', 'over-ons': '/over-ons', workshops: '/workshops', 'steun-ons': '/steun-ons', 'doe-mee': '/doe-mee', contact: '/contact', factchecks: '/factchecks', 'the-bigger-picture': '',
  woorden: '/woorden', quotes: '/quotes', infographics: '/infographics', games: '/games', woordenboek: '/woordenboek', dossiers: '/dossiers',
  downloads: '/downloads-en-tools', 'gids-zelf-checken': '/hoe-check-je-zelf-een-bericht', 'gids-ai-beelden': '/hoe-herken-je-ai-beelden',
  'eerste-hulp': '/eerste-hulp', 'factchecks-van-de-week': '/factchecks-van-de-week', technieken: '/desinformatietechnieken',
  // Shown on the homepage only.
  'save-the-date': '/', poll: '/', barometer: '/',
  wedstrijd: '/wedstrijd', nieuwsbrief: '/nieuwsbrief',
};

/** The side list, in the order of the site's menu. */
const GROUPS: { title: string; slugs: string[] }[] = [
  { title: 'Algemeen', slugs: ['home', 'footer', 'blokken', 'factcheck-suggereren', 'nieuwsbrief'] },
  { title: 'Leren', slugs: ['the-bigger-picture', 'dossiers', 'downloads', 'gids-zelf-checken', 'gids-ai-beelden', 'woordenboek', 'games'] },
  { title: 'Actua', slugs: ['factchecks', 'factchecks-van-de-week', 'technieken', 'save-the-date', 'wedstrijd', 'poll'] },
  { title: 'Van de week', slugs: ['woorden', 'quotes', 'infographics', 'eerste-hulp', 'barometer'] },
  { title: 'Wat is desinformatie', slugs: ['desinformatie-wat-is-het', 'desinformatie-desinformatie-vs-misinformatie', 'desinformatie-waarom-geloven-we-het'] },
  { title: 'Over deCheckers', slugs: ['over-ons', 'workshops', 'steun-ons', 'doe-mee', 'contact'] },
  { title: 'Juridisch', slugs: ['privacy', 'cookies'] },
];

/**
 * Other names of a page for the page picker: the rubrieken on the homepage whose content the page
 * holds (card and section titles), other labels the site uses for it, and its former CMS name.
 */
const KEYWORDS: Record<string, string[]> = {
  home: ['Homepagina', 'Actua', 'Informatief', 'Zeker ook eens checken', 'Achter de schermen', 'Meedoen', 'Meet de factcheckers'],
  // Each block by where it shows: the hamburger menu, the green banner, the navy tip box.
  blokken: ['Vaste blokken', 'Menu', 'Hamburgermenu', 'Disclaimer', 'Sharing is caring', 'Banner Mens vs machine', 'Banner Mens of machine', 'Tip ons', 'Tipbox'],
  'factcheck-suggereren': ['Iets laten checken', 'Factcheck suggereren', 'Echt of bullshit'],
  'the-bigger-picture': ['Desinformatie doorheen de geschiedenis', 'Gebundelde factchecks'],
  'gids-zelf-checken': ['Zelf leren checken', 'Gids: zelf checken'],
  'gids-ai-beelden': ['Gids: AI-beelden', 'Praktische gidsen'],
  woordenboek: ['Woordenboek', 'Woordenlijst'],
  downloads: ['Downloads en tools'],
  // The homepage's technique cards show the technique of the current week.
  'factchecks-van-de-week': ['Factcheck van de week', 'Gebruikte techniek', 'Hoe detecteer je'],
  technieken: ['Gebruikte technieken'],
  woorden: ['Woorden'],
  quotes: ['Quotes'],
  infographics: ['Infographics'],
  barometer: ['Barometer'],
  'steun-ons': ['Steun ons', 'Doneren'],
  'doe-mee': ['Word vrijwilliger', 'Jongerenredactie'],
  wedstrijd: ['Wedstrijdreglement', 'Schiftingsvraag', 'Inzendingen', 'Winnaar'],
  nieuwsbrief: ['Teaser', 'Video nieuwsbrief'],
  privacy: ['Privacy'],
  cookies: ['Cookies'],
};

function page(slug: string): ContentPage {
  const keywords = KEYWORDS[slug] || [];
  const definition = definitions[slug];
  if (definition) return { name: definition.name, slug, path: PATHS[slug] ?? '', definition, keywords };
  const legacy = LEGACY[slug];
  return { name: legacy.name, slug, path: legacy.path, legacyFields: legacy.fields, keywords };
}

function normalise(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Whether a search in the page picker finds the page: every word in its name, slug or one keyword. */
export function pageMatches(page: ContentPage, search: string): boolean {
  const words = normalise(search).split(/\s+/).filter(Boolean);
  return [page.name, page.slug, ...page.keywords].map(normalise).some((text) => words.every((word) => text.includes(word)));
}

const grouped = new Set(GROUPS.flatMap((group) => group.slugs));
/** A page added to the site's schemas but not to a group still shows up. */
const ungrouped = [...Object.keys(definitions), ...Object.keys(LEGACY)].filter((slug) => !grouped.has(slug));

export const PAGE_GROUPS: PageGroup[] = [
  ...GROUPS.map((group) => ({ title: group.title, pages: group.slugs.filter((slug) => definitions[slug] || LEGACY[slug]).map(page) })),
  ...(ungrouped.length ? [{ title: 'Overige', pages: ungrouped.map(page) }] : []),
];

export const PAGES: ContentPage[] = PAGE_GROUPS.flatMap((group) => group.pages);

/** Starting values of an older page: its fields, empty or at the site's current text. */
export function emptyLegacy(page: ContentPage): Record<string, LegacyField> {
  const result: Record<string, LegacyField> = {};
  for (const field of page.legacyFields || []) result[field.key] = { type: field.type, value: field.initial || '' };
  return result;
}

/** Address of a page on the site; The bigger picture opens its first series. */
export function sitePath(page: ContentPage, content: unknown): string {
  if (page.slug !== 'the-bigger-picture') return page.path;
  const series = isRecord(content) && Array.isArray(content.series) ? content.series[0] : null;
  return isRecord(series) && typeof series.slug === 'string' && series.slug ? `/the-bigger-picture/${series.slug}` : '';
}

/** The public site of this environment. */
export const SITE_URL = (process.env.VUE_APP_SITE_URL || 'https://decheckers.be').replace(/\/$/, '');

/** A file of the site itself (`/about/team/jan.webp`) loads from the site; uploads already have a full address. */
export function mediaUrl(src: string): string {
  return src.startsWith('/') && !src.startsWith('//') ? `${SITE_URL}${src}` : src;
}
